import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export type ActivityPhase = 'workshop' | 'assess' | 'design' | 'implement' | 'validate' | 'document' | 'kt';

export interface ActivityDependency {
  id: string;
  activityId: string;
  predecessorId: string;
  dependencyType: 'FS' | 'SS' | 'FF';
  lagDays: number;
}

export interface VcfActivity {
  id: string;
  capabilityId: string;
  name: string;
  description: string | null;
  role: string;
  skill: string | null;
  skillId: string | null;
  baseDays: number;
  activityPhase: ActivityPhase | null;
  isMilestone: boolean;
  sortOrder: number;
  isActive: boolean;
  customerDays: number;
  customerRole: string | null;
  dependencies?: ActivityDependency[];
}

export interface VcfGuardrail {
  id: string;
  capabilityId: string;
  name: string;
  code: string;
  baseline: string | null;
  multiplier: string | null;
  baselineValue: number | null;
  multiplierFactor: number | null;
  multiplierStep: number | null;
  multiplierLabel: string | null;
  optionFactors: Record<string, number> | null;
  inputType: 'number' | 'select' | 'boolean';
  options: string[] | null;
  defaultValue: string | null;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
}

export interface VcfCapability {
  id: string;
  workstreamId: string;
  code: string;
  name: string;
  description: string | null;
  cls: 'CORE' | 'RECOMMENDED' | 'OPTIONAL' | null;
  defaultIncluded: boolean;
  sortOrder: number;
  isActive: boolean;
  scopeText: string | null;
  deliverables: string[] | null;
  prerequisites: string[] | null;
  activities?: VcfActivity[];
  guardrails?: VcfGuardrail[];
}

export interface VcfWorkstream {
  id: string;
  code: string;
  name: string;
  description: string | null;
  product: string | null;
  phase: 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise' | null;
  pillar: 'infrastructure' | 'application' | 'security' | null;
  category: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  capabilities?: VcfCapability[];
}

export interface VcfGeneralMultiplier {
  id: string;
  code: string;
  name: string;
  description: string | null;
  multiplier: string;
  appliesTo: string[] | null;
  sortOrder: number;
  isActive: boolean;
}

interface WorkstreamsResponse {
  workstreams: VcfWorkstream[];
}

interface WorkstreamResponse {
  workstream: VcfWorkstream;
}

// ============ Hooks ============

export function useWorkstreams(params?: {
  includeCapabilities?: boolean;
  includeActivities?: boolean;
  includeInactive?: boolean;
}) {
  return useQuery({
    queryKey: ['workstreams', params],
    queryFn: () =>
      api.get<WorkstreamsResponse>('/vcf/workstreams', {
        includeCapabilities: params?.includeCapabilities,
        includeActivities: params?.includeActivities,
        includeInactive: params?.includeInactive,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes - workstreams don't change often
  });
}

export function useWorkstream(id: string) {
  return useQuery({
    queryKey: ['workstreams', id],
    queryFn: () => api.get<WorkstreamResponse>(`/vcf/workstreams/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCapability(id: string) {
  return useQuery({
    queryKey: ['capabilities', id],
    queryFn: () =>
      api.get<{ capability: VcfCapability & { activities: VcfActivity[]; guardrails: VcfGuardrail[] } }>(
        `/vcf/capabilities/${id}`
      ),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}
